<template>
  <div class="row">
    <div class="col-12">
      <h2>{{ statsTitle }}</h2>
      <div class="mb-3">
        <div class="fw-semibold">{{ objectTitle }}</div>
      </div>
    </div>
    <div class="col-12 col-xl-8">
      <div class="card shadow-sm mb-4">
        <div class="card-body">
          <div class="row g-3 align-items-end">
            <div class="col-12 col-lg-8">
              <label for="object-stats-range" class="form-label">Zeitraum</label>
              <DaterangePicker id="object-stats-range" v-model="dateRange" placeholder="Zeitraum auswählen" />
            </div>
            <div class="col-12 col-lg-4">
              <label for="object-stats-interval" class="form-label">Intervall</label>
              <select id="object-stats-interval" v-model="statsStore.interval" class="form-control">
                <option v-for="option in intervalOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div class="card shadow-sm mb-4">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h3 class="h5 mb-0">Interaktionen im Zeitverlauf</h3>
          <small class="text-muted">{{ dateLabel }}</small>
        </div>
        <div class="card-body">
          <div v-if="statsStore.loading" class="text-muted">Lade Statistik...</div>
          <div v-else-if="!statsData" class="text-muted">Keine Daten verfügbar.</div>
          <StatsTimeSeriesChart
              v-else
              :buckets="histogramBuckets"
              :interval="statsStore.interval"
              series-name="Interaktionen"
              :primary-color="chartPrimaryColor"
              :trend-color="chartSecondaryColor"
          />
        </div>
      </div>
    </div>
    <div class="col-12 col-xl-4">
      <div class="card shadow-sm mb-4">
        <div class="card-header">
          <h3 class="h5 mb-0">Überblick</h3>
        </div>
        <div class="card-body">
          <div class="stat-highlight">
            <small class="text-uppercase text-muted d-block">Gesamtinteraktionen</small>
            <strong>{{ totalInteractions.toLocaleString() }}</strong>
          </div>
          <div class="stat-highlight mt-3">
            <small class="text-uppercase text-muted d-block">Ø Interaktionen pro {{ intervalLabel }}</small>
            <strong>{{ averageInteractionsPerPeriod.toLocaleString(undefined, {maximumFractionDigits: 1}) }}</strong>
          </div>
          <div class="stat-highlight mt-3">
            <small class="text-uppercase text-muted d-block">Eindeutige Seitenaufrufe</small>
            <strong>{{ totalUniquePageViews.toLocaleString() }}</strong>
          </div>
        </div>
      </div>
      <div class="card shadow-sm mb-4">
        <div class="card-header">
          <h3 class="h5 mb-0">Top Aktionen</h3>
        </div>
        <div class="card-body">
          <DataTableHistogram
              v-if="actionTableData"
              :columns="actionColumns"
              :tableData="actionTableData"
              :enableSearch="false"
              :enableSize="false"
              :extraHeader="false"
              :showRowNumbers="true"
          />
        </div>
      </div>
    </div>
    <div class="col-12">
      <div class="card shadow-sm mb-4">
        <div class="card-header">
          <h3 class="h5 mb-0">Top Seiten</h3>
        </div>
        <div class="card-body">
          <DataTableHistogram
              v-if="urlTableData"
              :columns="urlColumns"
              :tableData="urlTableData"
              :enableSearch="false"
              :enableSize="false"
              :extraHeader="false"
              :showRowNumbers="true"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {useLocalizedObjectStore} from "@/stores/localized_objects";
import {useStatsStore} from "@/stores/stats";
import DaterangePicker from "@/components/DaterangePicker.vue";
import DataTableHistogram from "@/components/DataTableHistogram.vue";
import StatsTimeSeriesChart from "@/components/stats/StatsTimeSeriesChart.vue";
import dayjs from "dayjs";
import {humanizeAction} from "@/stores/tracking_descriptions";
import {getBaseTypeTheme, resolveBaseTypeKey} from "@/utils/baseTypeTheme";

export default {
  name: "ObjectsStats",
  components: {
    DaterangePicker,
    DataTableHistogram,
    StatsTimeSeriesChart,
  },
  setup() {
    return {
      localizedObjectStore: useLocalizedObjectStore(),
      statsStore: useStatsStore(),
    }
  },
  data() {
    return {
      object: null,
      statsData: null,
      intervalOptions: [
        {label: 'Tag', value: '1d'},
        {label: 'Woche', value: '1w'},
        {label: 'Monat', value: '1M'},
        {label: 'Quartal', value: '1q'},
        {label: 'Jahr', value: '1y'},
      ],
    }
  },
  created() {
    this.loadObject().then(() => {
      this.loadStats()
    })
  },
  watch: {
    'statsStore.interval'() {
      this.loadStats()
    }
  },
  computed: {
    dateRange: {
      get() {
        return {
          from: this.statsStore.from,
          to: this.statsStore.to,
        }
      },
      set(value) {
        if (!value?.from?.dateInstance || !value?.to?.dateInstance) {
          return
        }
        this.statsStore.from = dayjs(value.from.dateInstance).valueOf()
        this.statsStore.to = dayjs(value.to.dateInstance).valueOf()
        this.loadStats()
      }
    },
    histogramBuckets() {
      return this.statsData?.aggregations?.histogram?.buckets || []
    },
    resolvedBaseType() {
      return resolveBaseTypeKey(this.$route.name?.split('.')[0] || 'accommodations')
    },
    statsTitle() {
      return `${getBaseTypeTheme(this.resolvedBaseType).label}-Statistik`
    },
    objectTitle() {
      const shortLabel = getBaseTypeTheme(this.resolvedBaseType).shortLabel
      return this.object?.name || `${shortLabel} #${this.$route.params.id}`
    },
    chartPrimaryColor() {
      return getBaseTypeTheme(this.resolvedBaseType).primary
    },
    chartSecondaryColor() {
      return getBaseTypeTheme(this.resolvedBaseType).secondary
    },
    totalInteractions() {
      return this.histogramBuckets.reduce((sum, bucket) => sum + (bucket.doc_count || 0), 0)
    },
    totalUniquePageViews() {
      return this.urlBuckets.reduce((sum, bucket) => sum + (bucket.unique_hashes?.value || 0), 0)
    },
    averageInteractionsPerPeriod() {
      if (!this.histogramBuckets.length) {
        return 0
      }
      return this.totalInteractions / this.histogramBuckets.length
    },
    urlBuckets() {
      return this.statsData?.aggregations?.urls?.buckets?.buckets || []
    },
    actionBuckets() {
      return this.statsData?.aggregations?.actions?.buckets || []
    },
    intervalLabel() {
      const labels = {
        '1d': 'Tag',
        '1w': 'Woche',
        '1M': 'Monat',
        '1q': 'Quartal',
        '1y': 'Jahr',
      }
      return labels[this.statsStore.interval] || 'Zeitraum'
    },
    dateLabel() {
      return `${dayjs(this.statsStore.from).format('DD.MM.YYYY')} bis ${dayjs(this.statsStore.to).format('DD.MM.YYYY')}`
    },
    urlColumns() {
      return [
        {
          label: 'Seite',
          field: 'key',
        },
        {
          label: 'Interaktionen',
          field: 'value',
        },
        {
          label: 'Eindeutig',
          field: 'unique',
        },
      ]
    },
    actionColumns() {
      return [
        {
          label: 'Aktion',
          field: 'key',
        },
        {
          label: 'Interaktionen',
          field: 'value',
        },
      ]
    },
    urlTableData() {
      const rows = this.urlBuckets.map((bucket) => ({
        key: String(bucket.key),
        data: {
          value: bucket.doc_count,
          unique: bucket.unique_hashes?.value || 0,
        }
      }))

      return {
        rows,
        value: {
          total: rows.reduce((sum, row) => sum + (row.data?.value || 0), 0),
          compareTotal: 0,
        },
        unique: {
          total: rows.reduce((sum, row) => sum + (row.data?.unique || 0), 0),
          compareTotal: 0,
        }
      }
    },
    actionTableData() {
      const rows = this.actionBuckets.map((bucket) => ({
        key: humanizeAction(bucket.key),
        data: {
          value: bucket.doc_count,
        }
      }))

      return {
        rows,
        value: {
          total: rows.reduce((sum, row) => sum + (row.data?.value || 0), 0),
          compareTotal: 0,
        },
      }
    },
  },
  methods: {
    loadObject() {
      return this.localizedObjectStore.apiShow(this.$route.params.id, 'de').then(({data}) => {
        this.object = data
        return data
      })
    },
    loadStats() {
      this.statsStore.fetchData('object_interactions', {
        from: this.statsStore.from,
        to: this.statsStore.to,
        interval: this.statsStore.interval,
        products: this.$route.params.id,
      }).then(({data}) => {
        this.statsData = data
      })
    },
  },
}
</script>

<style scoped lang="scss">
.stat-highlight strong {
  font-size: 2rem;
  line-height: 1;
}
</style>
